import { httpClient } from '@/core/api/httpClient';
import type {
  Board,
  BoardDetailDto,
  CreatedResponse,
  CreateBoardPayload,
  UpdateBoardPayload,
  ReorderPayload,
} from '../types';
import { mockBoards } from './boards.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

const boardsMock: Board[] = [...mockBoards];

export const boardsService = {
  getById: async (boardId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const board = boardsMock.find((b) => b.id === boardId);
      const detail: BoardDetailDto = board
        ? { ...board, views: [], statuses: [], updatedAt: null }
        : {
            id: boardId,
            projectId: null,
            name: null,
            description: null,
            order: 0,
            views: [],
            statuses: [],
            createdAt: '',
            updatedAt: null,
          };
      return Promise.resolve({ data: detail });
    }
    return httpClient.get<BoardDetailDto>(`${API_V1}/boards/${boardId}`, { signal });
  },

  getByProject: async (projectId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: boardsMock.filter((b) => b.projectId === projectId) });
    }
    return httpClient.get<Board[]>(`${API_V1}/projects/${projectId}/boards`, { signal });
  },

  create: async (projectId: string, data: CreateBoardPayload) => {
    if (USE_MOCK) {
      const newBoard: Board = {
        id: `board-${Date.now()}`,
        projectId,
        tenantId: 'org-1',
        name: data.name,
        description: data.description ?? null,
        order: boardsMock.filter((b) => b.projectId === projectId).length,
        statusCount: 0,
        createdAt: new Date().toISOString(),
      };
      boardsMock.push(newBoard);
      return Promise.resolve({ data: { id: newBoard.id } as CreatedResponse });
    }
    return httpClient.post<CreateBoardPayload, CreatedResponse>(
      `${API_V1}/projects/${projectId}/boards`,
      data,
    );
  },

  update: async (boardId: string, data: UpdateBoardPayload) => {
    if (USE_MOCK) {
      const idx = boardsMock.findIndex((b) => b.id === boardId);
      if (idx !== -1) boardsMock[idx] = { ...boardsMock[idx], ...data };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<UpdateBoardPayload, void>(`${API_V1}/boards/${boardId}`, data);
  },

  delete: async (boardId: string) => {
    if (USE_MOCK) {
      const idx = boardsMock.findIndex((b) => b.id === boardId);
      if (idx !== -1) boardsMock.splice(idx, 1);
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/boards/${boardId}`);
  },

  reorder: async (projectId: string, data: ReorderPayload) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.patch<ReorderPayload, void>(
      `${API_V1}/projects/${projectId}/boards/reorder`,
      data,
    );
  },
};
